// Developer panel (F9, or the Dev button for developer accounts): turn simulated players on and off and watch
// what it costs the server and this device. Simulated players are visible to everyone in the zone.
import type { ServerMsg, SimSettings } from '../../../shared/protocol.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
type DevState = Extract<ServerMsg, { type: 'dev_state' }>;
export interface ClientStats { fps: number; worstFrameMs: number; onScreen: number; corrections: number; snaps: number; ping: number; sounds: number }

export class DevPanel {
  enabled = false;
  onSim: (s: SimSettings) => void = () => {};
  onStats: () => void = () => {};
  onSky: (hour: number | null, weather: string | null) => void = () => {};
  onGo: (zone: string) => void = () => {};
  onCompanions: (count: number) => void = () => {};
  client: () => ClientStats = () => ({ fps: 0, worstFrameMs: 0, onScreen: 0, corrections: 0, snaps: 0, ping: 0, sounds: 0 });
  private el = $('devPanel'); private timer = 0; private sync = true;   // take the server's settings into the checkboxes once

  constructor() {
    $('devBtn').addEventListener('click', () => this.toggle());
    $('devClose').addEventListener('click', () => this.toggle(false));
    $('devApply').addEventListener('click', () => { this.onSim(this.read()); this.sync = true; });
    $('devOff').addEventListener('click', () => { $<HTMLInputElement>('devCount').value = '0'; this.syncCount(); this.onSim(this.read()); this.sync = true; });
    $('devCount').addEventListener('input', () => this.syncCount());
    for (const b of this.el.querySelectorAll<HTMLButtonElement>('[data-n]')) b.addEventListener('click', () => { $<HTMLInputElement>('devCount').value = b.dataset.n!; this.syncCount(); this.onSim(this.read()); this.sync = true; });
    const sky = () => { const h = $<HTMLSelectElement>('devHour').value, w = $<HTMLSelectElement>('devWeather').value; this.onSky(h === '' ? null : Number(h), w || null); };
    for (const b of this.el.querySelectorAll<HTMLButtonElement>('[data-go]')) b.addEventListener('click', () => this.onGo(b.dataset.go!));
    for (const b of this.el.querySelectorAll<HTMLButtonElement>('[data-comp]')) b.addEventListener('click', () => this.onCompanions(Number(b.dataset.comp)));
    $('devHour').addEventListener('change', sky); $('devWeather').addEventListener('change', sky);
    addEventListener('keydown', e => { if (e.code === 'F9' && this.enabled) { e.preventDefault(); this.toggle(); } });
  }

  setEnabled(on: boolean): void { this.enabled = on; $('devBtn').hidden = !on; if (!on) this.toggle(false); }
  get open(): boolean { return !this.el.hidden; }
  toggle(force?: boolean): void {
    const open = force ?? (this.el.hidden && this.enabled);
    this.el.hidden = !open; clearInterval(this.timer);
    if (open) { this.sync = true; this.onStats(); this.timer = window.setInterval(() => this.onStats(), 1000); }
  }

  private syncCount(): void { $('devCountOut').textContent = $<HTMLInputElement>('devCount').value; }
  private read(): SimSettings {
    const c = (id: string) => $<HTMLInputElement>(id).checked;
    return { count: Number($<HTMLInputElement>('devCount').value), chatter: c('devChatter'), dash: c('devDash'), stall: c('devStall'), spread: c('devSpread'), overfill: c('devOverfill'), world: c('devWorld'), fight: c('devFight') };
  }

  set(m: DevState): void {
    $<HTMLInputElement>('devCount').max = String(m.simMax);
    // take the server's numbers into the controls only when the panel opens or after Apply,
    // so a count you picked isn't reset by the next stats update before you press Apply
    if (this.sync) { this.sync = false; $<HTMLInputElement>('devCount').value = String(m.simCount || 50); this.syncCount(); for (const [id, k] of [['devChatter', 'chatter'], ['devDash', 'dash'], ['devStall', 'stall'], ['devSpread', 'spread'], ['devOverfill', 'overfill'], ['devWorld', 'world'], ['devFight', 'fight']] as const) $<HTMLInputElement>(id).checked = !!m.sim[k]; }
    const s = m.stats, c = this.client(), warn = (v: number, lim: number) => v > lim ? ' ⚠' : '';
    $('devStats').textContent = [
      `server   ${s.players} players + ${m.simCount} simulated`,
      `layers   ${Object.entries(m.layers).map(([z, l]) => `${z}: ${l.join(' · ')}`).join('  ') || '-'}`,
      `tick     ${s.tickMs.p50} ms typical · ${s.tickMs.p99} ms worst 1%${warn(s.tickMs.p99, 16.7)}  (budget 16.7)`,
      `cpu      ${s.cpuPercent}% of one core${warn(s.cpuPercent, 80)} · ${s.memoryMB} MB`,
      `network  ${s.outKBps} KB/s out · ${s.msgsPerSec} msgs/s`,
      `combat   ${s.battles} battles · ${s.mobs} creatures alive`,
      `this     ${c.fps} fps · worst frame ${c.worstFrameMs} ms${warn(c.worstFrameMs, 50)} · ${c.onScreen} on screen`,
      `         ping ${c.ping} ms · corrections ${c.corrections} (snapped ${c.snaps}) · ${c.sounds} sounds playing`,
    ].join('\n');
  }
}
